import React, { useState, useEffect, useCallback } from 'react'
import { shape, string, array } from 'prop-types'
import styles from './VideoPlayer.module.scss'
import Container from '@/component/Primitive/Container'
import Stack from '@/component/Primitive/Stack'
import Type from '@/component/Primitive/Type'
import Hide from '@/component/Primitive/Hide'
import SmartLink from '@/component/Primitive/SmartLink'
import Icon from '@/component/Primitive/Icon'
import moment from 'moment'
import { EmbedWrapper, Description, Image, LocationSelect } from './components'
import cx from 'classnames'

const views = {
  COVER: 'COVER',
  VIDEO: 'VIDEO'
}

const status = {
  NOT_STARTED: 'NOT_STARTED',
  IN_PROGRESS: 'IN_PROGRESS',
  UP_NEXT: 'UP_NEXT',
  ENDED: 'ENDED',
  LOADING: 'LOADING',
  ERROR: 'ERROR',
  IDLE: 'IDLE'
}

const LiveComponent = ({
  eventName,
  eventFullUrlPath,
  descriptionDetails,
  locations,
  agendaSnapshot,
  fullAgenda
}) => {
  const [liveStreamStatus, setLiveStreamStatus] = useState(
    agendaSnapshot.info.liveStreamStatus
  )

  /**
   * Checks if the current live stream status matches any of the provided statuses.
   *
   * @param {('NOT_STARTED'|'IN_PROGRESS'|'UP_NEXT'|'ENDED'|'LOADING'|'ERROR')|Array<'NOT_STARTED'|'IN_PROGRESS'|'UP_NEXT'|'ENDED'|'LOADING'|'ERROR'>} statuses
   * - A status or an array of statuses to check against the current live stream status. Must be one or more of the following:
   * 'NOT_STARTED', 'IN_PROGRESS', 'UP_NEXT', 'ENDED', 'LOADING', 'ERROR'.
   * @returns {boolean} - Returns true if the current live stream status matches any of the provided statuses, otherwise false.
   */
  const isLS = (statuses) => {
    if (!Array.isArray(statuses)) {
      statuses = [statuses]
    }
    const statusValues = statuses.map((s) => status[s]).filter(Boolean)
    return statusValues.includes(liveStreamStatus)
  }

  /**
   * Checks if the current live stream status matches any of the provided statuses.
   *
   * @param {('NOT_STARTED'|'IN_PROGRESS'|'UP_NEXT'|'ENDED'|'LOADING'|'ERROR')|Array<'NOT_STARTED'|'IN_PROGRESS'|'UP_NEXT'|'ENDED'|'LOADING'|'ERROR'>} statuses
   * - A status or an array of statuses to check against the current live stream status. Must be one or more of the following:
   * 'NOT_STARTED', 'IN_PROGRESS', 'UP_NEXT', 'ENDED', 'LOADING', 'ERROR'.
   * @returns {boolean} - Returns false if the current live stream status matches any of the provided statuses, otherwise false.
   */
  const isNotLS = (statuses) => !isLS(statuses)

  const [displayedLocation, setDisplayedLocation] = useState(() => {
    switch (agendaSnapshot.info.liveStreamStatus) {
      case status.NOT_STARTED:
        return locations?.[0]
      case status.IN_PROGRESS:
        return locations.find(
          (l) => l.name === agendaSnapshot.currentAgenda[0].location.name
        )
      case status.UP_NEXT:
        return locations.find(
          (l) => l.name === agendaSnapshot.nextAgenda[0].location.name
        )
      case status.ENDED:
        return locations?.[0]
      default:
        return null
    }
  })

  const [displayedAgendaItem, setDisplayedAgendaItem] = useState(() => {
    switch (agendaSnapshot.info.liveStreamStatus) {
      case status.NOT_STARTED:
        return null
      case status.IN_PROGRESS:
        return agendaSnapshot.currentAgenda[0]
      case status.UP_NEXT:
        return agendaSnapshot.nextAgenda[0]
      case status.ENDED:
        return null
      default:
        return null
    }
  })

  const [videoPlayerView, setVideoPlayerView] = useState(() => {
    switch (agendaSnapshot.info.liveStreamStatus) {
      case status.NOT_STARTED:
        return views.COVER
      case status.IN_PROGRESS:
        return views.VIDEO
      case status.UP_NEXT:
        return views.COVER
      case status.ENDED:
        return views.COVER
      default:
        return views.COVER
    }
  })

  const getStreamTitle = () => {
    switch (liveStreamStatus) {
      case status.NOT_STARTED:
        return `${eventName} ${displayedLocation.name} is starting soon`
      case status.IN_PROGRESS:
        return displayedAgendaItem.name
      case status.UP_NEXT:
        return displayedAgendaItem.name
      case status.ENDED:
        return 'Thank you for watching'
      default:
        return null
    }
  }

  const getStreamPretext = () => {
    switch (liveStreamStatus) {
      case status.NOT_STARTED:
        return 'UPCOMING'
      case status.IN_PROGRESS:
        return 'LIVE'
      case status.UP_NEXT:
        return 'UP NEXT'
      case status.ENDED:
        return 'STAGE ENDED'
      default:
        return null
    }
  }

  const getCompletedAgendaCount = () => {
    const now = new Date()
    return fullAgenda.filter((a) => {
      return moment(now).isAfter(a.endDate)
    }).length
  }

  const getLivestreamProperties = (location) => {
    if (!location)
      return {
        updatedStatus: status.ERROR,
        currentAgendaItem: null,
        nextAgendaItem: null,
        errorMessage: 'No location found'
      }

    const now = new Date()

    const stageSpecificAgenda = fullAgenda.filter(
      (a) => a.location.name === location.name
    )

    if (!stageSpecificAgenda.length)
      return {
        updatedStatus: status.ERROR,
        currentAgendaItem: null,
        nextAgendaItem: null,
        errorMessage: 'No agenda items found for this location'
      }

    const currentAgenda = stageSpecificAgenda.filter((item) => {
      const isLive = moment(now).isBetween(item.startDate, item.endDate)
      return isLive
    })
    const nextAgenda = stageSpecificAgenda.filter((item) => {
      const isUpcoming = moment(now).isBefore(item.startDate)
      return isUpcoming
    })

    // Get status
    let updatedStatus
    const completedAgendaCount = getCompletedAgendaCount()
    if (!nextAgenda.length && !currentAgenda.length && completedAgendaCount > 0)
      updatedStatus = status.ENDED
    else if (currentAgenda.length) updatedStatus = status.IN_PROGRESS
    else if (nextAgenda.length && completedAgendaCount > 0)
      updatedStatus = status.UP_NEXT
    else if (nextAgenda.length && completedAgendaCount === 0)
      updatedStatus = status.NOT_STARTED
    else updatedStatus = status.ERROR

    switch (updatedStatus) {
      case status.NOT_STARTED:
        return {
          updatedStatus,
          currentAgendaItem: null,
          nextAgendaItem: nextAgenda?.[0]
        }
      case status.IN_PROGRESS:
        return {
          updatedStatus,
          currentAgendaItem: currentAgenda[0],
          nextAgendaItem: nextAgenda?.[0]
        }
      case status.UP_NEXT:
        return {
          updatedStatus,
          currentAgendaItem: null,
          nextAgendaItem: nextAgenda[0]
        }
      case status.ENDED:
        return {
          updatedStatus,
          currentAgendaItem: null,
          nextAgendaItem: null
        }
    }
  }

  const resetPlayerState = () => {
    setIsLocationSelectDisabled(false)
  }

  const switchLocation = (location) => {
    // Update the status

    const {
      updatedStatus,
      currentAgendaItem,
      nextAgendaItem,
      errorMessage
    } = getLivestreamProperties(location)

    if (updatedStatus === status.ERROR || errorMessage) {
      resetPlayerState()
      alert('An error occurred: ' + errorMessage)
      return
    }

    setLiveStreamStatus(status.LOADING)
    setDisplayedLocation(location)

    updateDisplayedAgendaItem(
      updatedStatus,
      currentAgendaItem,
      nextAgendaItem,
      location
    )
  }

  const handleLocationSwitchCleanup = (agendaItem, status) => {
    setDisplayedAgendaItem(agendaItem)
    if (status) {
      const timeout = setTimeout(() => {
        setLiveStreamStatus(status)
        setIsLocationSelectDisabled(false)
      }, 2000)
      return () => clearTimeout(timeout)
    }
  }

  const updateDisplayedAgendaItem = (
    s,
    currentAgendaItem,
    nextAgendaItem,
    location
  ) => {
    switch (s) {
      case status.NOT_STARTED:
        handleLocationSwitchCleanup(null, s)
        break
      case status.IN_PROGRESS:
        handleLocationSwitchCleanup(currentAgendaItem, s)
        break
      case status.UP_NEXT:
        handleLocationSwitchCleanup(nextAgendaItem, s)
        break
      case status.ENDED:
        handleLocationSwitchCleanup(null, s)
        break
      default:
        handleLocationSwitchCleanup(null, s)
    }
    if (videoPlayerView === views.VIDEO) triggerVideoLoad(location)
  }

  const [isLocationSelectDisabled, setIsLocationSelectDisabled] = useState(
    false
  )
  const disableSelect = () => setIsLocationSelectDisabled(true)
  const setSelectLocation = (locationVimeoId) => {
    const selectedLocation = locations.find(
      (l) => l.vimeoId === locationVimeoId
    )
    switchLocation(selectedLocation)
  }

  const [player, setPlayer] = useState(null)
  const [userState, setUserState] = useState({
    isHoveringOverVideo: true
  })

  useEffect(() => {
    if (!player)
      if (window.Vimeo) {
        setPlayer(new window.Vimeo.Player('vimeoVideo'))
      }
  }, [player])

  const triggerVideoLoad = useCallback((location) => {
    if (!window.Vimeo) {
      return
    }
    const player = new window.Vimeo.Player('vimeoVideo')
    player.loadVideo(location.vimeoId)
    player.play()
    setDisplayedLocation(location)
  }, [])

  const handleOpenVideo = () => {
    if (isLS(['NOT_STARTED', 'ENDED'])) return

    setVideoPlayerView(views.VIDEO)
    triggerVideoLoad(displayedLocation)
  }

  useEffect(() => {
    if (videoPlayerView.video) triggerVideoLoad(displayedLocation)
  }, [videoPlayerView, triggerVideoLoad, displayedLocation])

  return (
    <>
      <Container center gutter flex size="wide">
        <div
          className={cx(
            styles.Cover,
            videoPlayerView === views.VIDEO && styles.hide
          )}
        >
          {isLS(['NOT_STARTED', 'ENDED']) && (
            <>
              <Image
                device="tablet"
                image={agendaSnapshot.holdingImage.desktop}
              />
              <Image
                device="mobile"
                image={agendaSnapshot.holdingImage.mobile}
              />
            </>
          )}
          {isLS(['IN_PROGRESS', 'UP_NEXT']) && (
            <>
              <Image
                device="tablet"
                image={displayedAgendaItem.image.desktop}
              />
              <Image device="mobile" image={displayedAgendaItem.image.mobile} />
            </>
          )}
          {isLS('LOADING') && (
            <>
              <Image
                device="tablet"
                image={null}
                imageLabel={{
                  label: 'Switching stage',
                  stage: displayedLocation.name
                }}
              />
              <Image
                device="mobile"
                image={null}
                imageLabel={{
                  label: 'Switching stage',
                  stage: displayedLocation.name
                }}
              />
            </>
          )}
          <Container
            center
            gutter
            flex
            size="wide"
            className={styles.CoverInner}
          >
            <div className={styles.PlayButton}>
              <button
                onClick={() => handleOpenVideo()}
                className={cx(
                  styles.PlayButtonInner,
                  isLS(['NOT_STARTED', 'ENDED']) && styles.disabled
                )}
              >
                {isLS(['IN_PROGRESS', 'UP_NEXT']) && (
                  <Icon
                    type="play-square"
                    width={60}
                    height={60}
                    className={cx(styles.PlayButtonIcon)}
                  />
                )}
                <Stack gap="tiny" className={styles.PlayButtonText}>
                  {isNotLS('LOADING') && (
                    <Type
                      className={styles.EventName}
                      as="h2"
                      size={['body4', 'body3']}
                      weight="medium"
                    >
                      {eventName}
                    </Type>
                  )}
                  <Type as="h1" size={['heading8', 'heading4']} weight="medium">
                    {getStreamTitle()}
                  </Type>
                </Stack>
              </button>

              {isLS([
                'IDLE',
                'NOT_STARTED',
                'IN_PROGRESS',
                'UP_NEXT',
                'ENDED'
              ]) && (
                <LocationSelect
                  locations={locations}
                  setLocation={setSelectLocation}
                  displayedLocation={displayedLocation}
                  disabled={isLocationSelectDisabled}
                  disableSelect={disableSelect}
                />
              )}
            </div>
          </Container>
        </div>
      </Container>
      <div
        className={cx(
          styles.VideoPlayer,
          videoPlayerView === views.COVER && styles.hide
        )}
      >
        <Container
          center
          gutter
          flex
          size="mediumLarge"
          className={styles.VideoPlayerInner}
        >
          <Stack gap="small" className={styles.VideoPlayerStack}>
            <div
              className={styles.VideoEmbed}
              onMouseOver={() =>
                setUserState({ ...userState, isHoveringOverVideo: true })
              }
              onFocus={() =>
                setUserState({ ...userState, isHoveringOverVideo: true })
              }
              onMouseOut={() =>
                setUserState({ ...userState, isHoveringOverVideo: false })
              }
              onBlur={() =>
                setUserState({ ...userState, isHoveringOverVideo: false })
              }
            >
              {isLS([
                'IDLE',
                'NOT_STARTED',
                'IN_PROGRESS',
                'UP_NEXT',
                'ENDED'
              ]) && (
                <Hide below="tablet">
                  <div
                    className={cx(
                      styles.LocationSelectWrapper,
                      userState.isHoveringOverVideo && styles.visible
                    )}
                  >
                    <LocationSelect
                      locations={locations}
                      setLocation={setSelectLocation}
                      displayedLocation={displayedLocation}
                      disabled={isLocationSelectDisabled}
                      disableSelect={disableSelect}
                      className={styles.SelectControlWrapper}
                    />
                  </div>
                </Hide>
              )}
              {isLS(['NOT_STARTED', 'ENDED']) && (
                <>
                  <Image
                    device="tablet"
                    image={agendaSnapshot.holdingImage.desktop}
                    classNames={{
                      container: styles.HoldingImageWrapper
                    }}
                  />
                  <Image
                    device="mobile"
                    image={agendaSnapshot.holdingImage.mobile}
                    classNames={{
                      container: styles.HoldingImageWrapper
                    }}
                  />
                </>
              )}
              {isLS('LOADING') && (
                <>
                  <Image
                    device="tablet"
                    image={null}
                    classNames={{
                      container: styles.LoadingImageWrapper
                    }}
                    imageLabel={{
                      label: 'Switching stage',
                      stage: displayedLocation.name
                    }}
                    options={{ ignoreWrapper: true }}
                  />
                  <Image
                    device="mobile"
                    image={null}
                    classNames={{
                      container: styles.LoadingImageWrapper
                    }}
                    imageLabel={{
                      label: 'Switching stage',
                      stage: displayedLocation.name
                    }}
                    options={{ ignoreWrapper: true }}
                  />
                </>
              )}
              <EmbedWrapper
                type="vimeo"
                id="vimeoVideo"
                vimeoId={displayedLocation?.vimeoId}
                className={cx(styles.Embed, isLS(['LOADING']) && styles.hide)}
              />
            </div>
            <div className={styles.TitleWrapper}>
              <div className={styles.Title}>
                <Type
                  as="h1"
                  size={['heading8', 'heading6']}
                  weight="medium"
                  className={styles.Name}
                >
                  <span>
                    {getStreamPretext() && (
                      <span className={styles.LiveText}>
                        {getStreamPretext()}
                      </span>
                    )}
                    {getStreamTitle()}
                  </span>
                </Type>
              </div>
              <div className={styles.Subtitle}>
                {isNotLS('LOADING') && (
                  <SmartLink
                    to="/events[slug][oslug]"
                    as={`/${eventFullUrlPath}`}
                    className={styles.EventName}
                  >
                    <Type as="h2" size={['body4', 'body3']} weight="medium">
                      {eventName}
                    </Type>
                  </SmartLink>
                )}
              </div>
            </div>
            {isLS(['IN_PROGRESS', 'UP_NEXT']) && (
              <Description
                descriptionDetails={descriptionDetails}
                description={displayedAgendaItem?.description}
              />
            )}

            {isLS([
              'IDLE',
              'NOT_STARTED',
              'IN_PROGRESS',
              'UP_NEXT',
              'ENDED'
            ]) && (
              <Hide at="tablet">
                <LocationSelect
                  locations={locations}
                  setLocation={setSelectLocation}
                  displayedLocation={displayedLocation}
                  disabled={isLocationSelectDisabled}
                  disableSelect={disableSelect}
                  className={styles.SelectControlWrapper}
                />
              </Hide>
            )}
          </Stack>
        </Container>
      </div>
    </>
  )
}

LiveComponent.propTypes = {
  eventName: string,
  eventFullUrlPath: string,
  descriptionDetails: string,
  locations: array,
  agendaSnapshot: shape({
    currentAgenda: array,
    nextAgenda: array,
    info: shape({
      liveStreamStatus: string,
      completedAgendaCount: string
    })
  }),
  fullAgenda: array
}

export default LiveComponent
